<template>
  <el-card>
    <el-form label-position="top" :inline="true" size="default">
      <el-collapse v-model="activePanels">
        <el-collapse-item name="filters">
          <template #title>
            <h3>Filters</h3>
          </template>
          <el-row :gutter="10">
            <el-col :span="4" :xs="12">
              <el-form-item label="Website">
                <el-input
                  v-model="filter.website"
                  placeholder="Website"
                  @keyup.enter="update"
                />
              </el-form-item>
            </el-col>
            <el-col :span="4" :xs="12">
              <el-form-item label="Ad type">
                <AdTypeSelect
                  v-model="filter.type_id"
                  :lables="FlatDealAdTypeLabels"
                  :with-empty="false"
                  @update:modelValue="update"
                />
              </el-form-item>
            </el-col>
            <el-col :span="4" :xs="12">
              <el-form-item label="Size">
                <SizeSelect
                  v-model="filter.size_id"
                  :disabled="filter.type_id !== AdType.Banner"
                  @update:modelValue="update"
                />
              </el-form-item>
            </el-col>
            <el-col :span="4" :xs="12">
              <el-form-item label="Stream">
                <StreamSelect
                  v-model="filter.stream"
                  @update:modelValue="update"
                />
              </el-form-item>
            </el-col>
            <el-col :span="8" :xs="12">
              <el-form-item label="Spots">
                <SpotsSelector
                  v-model="filter.spot_ids"
                  @update:modelValue="update"
                />
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="10">
            <el-col :span="4" :xs="12" :sm="8" :md="4">
              <el-form-item label="Country">
                <CountrySelect
                  v-model="filter.countries"
                  :items="countries"
                  :multiple="true"
                  :use-id="true"
                  @update:modelValue="update"
                />
              </el-form-item>
            </el-col>
            <el-col :span="4" :xs="12" :sm="8" :md="4">
              <DeviceSelector
                v-model="filter.devices"
                @update:modelValue="update"
              />
            </el-col>
            <el-col :span="4" :xs="12" :sm="8" :md="4">
              <el-form-item label="Deal Type">
                <FlatDealTypeSelect
                  v-model="filter.deal_types"
                  @update:modelValue="update"
                />
              </el-form-item>
            </el-col>
            <el-col :span="8" :xs="24" :sm="12" :md="8">
              <el-form-item label="Period">
                <DateRangePicker
                  v-model="range"
                  :force-desktop="true"
                  @update:modelValue="update"
                />
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="10" class="mt-1">
            <el-button
              type="primary"
              :icon="isDesktop ? 'RefreshElementIcon' : ''"
              round
              size="small"
              @click="update"
            >
              Refresh
            </el-button>
            <el-button
              type="success"
              :icon="isDesktop ? 'CloseElementIcon' : ''"
              round
              size="small"
              @click="reset"
            >
              Clear filters
            </el-button>
            <el-button
              type="primary"
              size="small"
              class="fl-r"
              @click="downloadBuffet()"
            >
              Download CSV
            </el-button>
            <el-button
              type="primary"
              size="small"
              class="fl-r"
              @click="toggleChat(true)"
            >
              Request a Flat Deal
            </el-button>
          </el-row>
        </el-collapse-item>
      </el-collapse>
    </el-form>
    <RequestDealModal :visible="showDealRequest" @close="toggleChat(false)" />
  </el-card>
</template>

<script lang="ts">
import { defineComponent, ref, computed } from 'vue'
import { useCountriesStore } from '@/store/modules/countries'
import { AdType } from '@/api/types'
import { FlatDealAdTypeLabels } from '@/apps/traforama/lables'
import AdTypeSelect from '@/components/Select/AdTypeSelect.vue'
import StreamSelect from '@/components/Select/StreamSelect.vue'
import DateRangePicker from '@/components/DateRangePicker.vue'
import SpotsSelector from '@/apps/traforama/components/SpotsSelector.vue'
import SizeSelect from '@/components/Select/SizeSelect.vue'
import DeviceSelector from '@/apps/traforama/components/DeviceSelector.vue'
import FlatDealTypeSelect from '@/components/Select/FlatDealTypeSelect.vue'
import CountrySelect from '@/components/Select/CountrySelect.vue'
import useResize from '@/hooks/useResize'
import RequestDealModal from '@/apps/traforama/views/flat_deals/components/RequestDealModal.vue'
import {
  useFlatDealsStore,
  defaultFilter
} from '@/apps/traforama/store/flatDeals'
import { exportCSVFile } from '@/utils/excel'

export default defineComponent({
  name: 'FilterPanel',
  components: {
    AdTypeSelect,
    StreamSelect,
    DeviceSelector,
    RequestDealModal,
    DateRangePicker,
    FlatDealTypeSelect,
    CountrySelect,
    SpotsSelector,
    SizeSelect
  },
  props: {
    withSelected: {
      type: Boolean,
      default: false
    },
    modelValue: {
      type: Object,
      required: true
    }
  },
  setup(props, context) {
    const countriesStore = useCountriesStore()
    const flatDealsStore = useFlatDealsStore()
    const countries = computed(() => countriesStore.sorted)
    const filter = ref({ ...props.modelValue })

    const { isDesktop } = useResize()

    const showDealRequest = ref(false)
    const activePanels = ref(isDesktop.value ? ['filters', 'pressets'] : [])
    const update = (newValue?: any) => {
      context.emit('update:modelValue', filter.value)
      context.emit('refresh')
    }

    const reset = async () => {
      context.emit('update:modelValue', defaultFilter)
      filter.value = { ...defaultFilter }
      update()
    }

    const range = computed({
      get: () => {
        return [filter.value.deal_start, filter.value.deal_stop]
      },
      set: (val) => {
        if (Array.isArray(val) && val.length === 2) {
          filter.value.deal_start = val[0]
          filter.value.deal_stop = val[1]
        } else {
          filter.value.deal_start = ''
          filter.value.deal_stop = ''
        }
      }
    })

    const toggleChat = (value: boolean) => {
      showDealRequest.value = value
    }

    const downloadBuffet = () => {
      flatDealsStore.FetchBuffetReport().then((response: any) => {
        exportCSVFile([], response, 'buffet_report', ';')
      })
    }

    return {
      FlatDealAdTypeLabels,
      update,
      filter,
      countries,
      AdType,
      reset,
      range,
      isDesktop,
      showDealRequest,
      toggleChat,
      activePanels,
      downloadBuffet
    }
  }
})
</script>

<style lang="scss" scoped>
.el-select {
  width: 100%;
}

:deep(.el-card__body) {
  padding: 0px 5px;
}

.el-form-item {
  width: 100%;
}

h3 {
  margin: 2px 8px 5px 8px;
  padding-top: 5px;
}

:deep(.selected-checkbox .el-form-item__label) {
  padding-right: 5px;
}

:deep(.el-collapse-item__content) {
  padding-bottom: 5px;
}

:deep(.el-form-item--small .el-form-item__label) {
  padding: 0;
}

:deep(.el-form-item--small.el-form-item) {
  margin-bottom: 0;
}
:deep(.el-collapse-item__wrap) {
  padding: 5px;
}
.el-row,
.el-collapse {
  width: 100%;
}
</style>
